import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { format, getDay, getHours, parseISO, startOfWeek, isBefore } from "date-fns";

interface Session {
  id: string;
  date: string;
  cancelled: boolean;
  clients: {
    name: string;
    client_type: string;
  } | null;
}

interface ScheduleHeatMapProps {
  sessions: Session[];
  weeksBack: number;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 14 }, (_, i) => i + 6); // 6am to 7pm

const ScheduleHeatMap = ({ sessions, weeksBack }: ScheduleHeatMapProps) => {
  // Filter to only past sessions
  const pastSessions = useMemo(() => {
    const now = new Date();
    return sessions.filter(s => isBefore(parseISO(s.date), now));
  }, [sessions]);

  // Build heat map data: count UNIQUE WEEKS per day/hour slot
  // This gives us the consistency rate (how often this slot is used)
  const heatMapData = useMemo(() => {
    const grid: Record<string, { 
      weeksUsed: Set<string>;
      clients: string[];
      totalSessions: number;
    }> = {};

    // Initialize all slots
    for (let day = 0; day < 7; day++) {
      for (const hour of HOURS) {
        const key = `${day}-${hour}`;
        grid[key] = { weeksUsed: new Set(), clients: [], totalSessions: 0 };
      }
    }

    // Count sessions per slot, tracking unique weeks
    for (const session of pastSessions) {
      const date = parseISO(session.date);
      const day = getDay(date);
      const hour = getHours(date);
      const weekKey = format(startOfWeek(date), "yyyy-MM-dd");

      if (hour >= 6 && hour <= 19) {
        const key = `${day}-${hour}`;
        if (grid[key]) {
          grid[key].weeksUsed.add(weekKey);
          grid[key].totalSessions++;
          if (session.clients?.name && !grid[key].clients.includes(session.clients.name)) {
            grid[key].clients.push(session.clients.name);
          }
        }
      }
    }

    // Convert to final format with consistency rate
    const result: Record<string, { 
      weeksUsedCount: number;
      consistencyRate: number; // 0.0 to 1.0 (% of weeks this slot was used)
      clients: string[];
      totalSessions: number;
    }> = {};

    for (const [key, data] of Object.entries(grid)) {
      result[key] = {
        weeksUsedCount: data.weeksUsed.size,
        consistencyRate: weeksBack > 0 ? data.weeksUsed.size / weeksBack : 0,
        clients: data.clients,
        totalSessions: data.totalSessions,
      };
    }

    return result;
  }, [pastSessions, weeksBack]);

  // Find max consistency for color scaling
  const maxConsistency = useMemo(() => {
    return Math.max(...Object.values(heatMapData).map(d => d.consistencyRate), 0.01);
  }, [heatMapData]);

  // Get color intensity based on consistency rate
  const getColorClass = (consistencyRate: number) => {
    if (consistencyRate === 0) return "bg-muted/30";
    const intensity = consistencyRate / maxConsistency;
    if (intensity <= 0.25) return "bg-primary/20";
    if (intensity <= 0.5) return "bg-primary/40";
    if (intensity <= 0.75) return "bg-primary/60";
    return "bg-primary/80";
  };

  // Calculate busiest times (most consistent slots)
  const busiestSlots = useMemo(() => {
    return Object.entries(heatMapData)
      .filter(([_, data]) => data.weeksUsedCount > 0)
      .sort((a, b) => b[1].consistencyRate - a[1].consistencyRate)
      .slice(0, 5)
      .map(([key, data]) => {
        const [day, hour] = key.split("-").map(Number);
        return {
          day: DAYS[day],
          hour: format(new Date().setHours(hour, 0), "h a"),
          weeksUsed: data.weeksUsedCount,
          consistencyPercent: Math.round(data.consistencyRate * 100),
        };
      });
  }, [heatMapData]);

  // Calculate quietest slots (potential opportunities) - weekday business hours only
  const quietestSlots = useMemo(() => {
    return Object.entries(heatMapData)
      .filter(([key]) => {
        const [day, hour] = key.split("-").map(Number);
        return day >= 1 && day <= 5 && hour >= 7 && hour <= 18;
      })
      .sort((a, b) => a[1].consistencyRate - b[1].consistencyRate)
      .slice(0, 5)
      .map(([key, data]) => {
        const [day, hour] = key.split("-").map(Number);
        return {
          day: DAYS[day],
          hour: format(new Date().setHours(hour, 0), "h a"),
          weeksUsed: data.weeksUsedCount,
          consistencyPercent: Math.round(data.consistencyRate * 100),
        };
      });
  }, [heatMapData]);

  return (
    <div className="space-y-6">
      {/* Heat Map Grid */}
      <Card className="glass-card border-0">
        <CardHeader>
          <CardTitle className="text-lg">Weekly Schedule Consistency</CardTitle>
          <CardDescription>
            How consistently each time slot is used over the last {weeksBack} weeks. Darker = more consistent.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Header Row */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] gap-1 mb-1">
                <div className="text-xs text-muted-foreground"></div>
                {DAYS.map((day) => (
                  <div key={day} className="text-xs font-medium text-center text-muted-foreground">
                    {day}
                  </div>
                ))}
              </div>

              {/* Hour Rows */}
              {HOURS.map((hour) => (
                <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] gap-1 mb-1">
                  <div className="text-xs text-muted-foreground flex items-center">
                    {format(new Date().setHours(hour, 0), "h a")}
                  </div>
                  {DAYS.map((_, dayIndex) => {
                    const key = `${dayIndex}-${hour}`;
                    const data = heatMapData[key];
                    const isWeekend = dayIndex === 0 || dayIndex === 6;

                    return (
                      <Tooltip key={key}>
                        <TooltipTrigger asChild>
                          <div
                            className={`
                              h-8 rounded-md cursor-pointer transition-all hover:ring-2 hover:ring-primary/50
                              ${getColorClass(data?.consistencyRate || 0)}
                              ${isWeekend ? "opacity-60" : ""}
                            `}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-sm">
                            <p className="font-medium">
                              {DAYS[dayIndex]}s, {format(new Date().setHours(hour, 0), "h:00")}-{format(new Date().setHours(hour + 1, 0), "h:00 a")}
                            </p>
                            <p className="text-muted-foreground">
                              Used {data?.weeksUsedCount || 0} of {weeksBack} weeks ({Math.round((data?.consistencyRate || 0) * 100)}%)
                            </p>
                            {data?.clients.length > 0 && (
                              <p className="text-xs mt-1">
                                Clients: {data.clients.slice(0, 3).join(", ")}
                                {data.clients.length > 3 && ` +${data.clients.length - 3} more`}
                              </p>
                            )}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}

              {/* Legend */}
              <div className="flex items-center justify-end gap-2 mt-4">
                <span className="text-xs text-muted-foreground">Never</span>
                <div className="flex gap-1">
                  <div className="w-4 h-4 rounded bg-muted/30" />
                  <div className="w-4 h-4 rounded bg-primary/20" />
                  <div className="w-4 h-4 rounded bg-primary/40" />
                  <div className="w-4 h-4 rounded bg-primary/60" />
                  <div className="w-4 h-4 rounded bg-primary/80" />
                </div>
                <span className="text-xs text-muted-foreground">Always</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insights Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Busiest Times */}
        <Card className="glass-card border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-primary" />
              Most Consistent Slots
            </CardTitle>
            <CardDescription>Time slots you use most reliably</CardDescription>
          </CardHeader>
          <CardContent>
            {busiestSlots.length === 0 ? (
              <p className="text-sm text-muted-foreground">No session data available</p>
            ) : (
              <div className="space-y-2">
                {busiestSlots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                  >
                    <div>
                      <span className="font-medium">{slot.day}</span>
                      <span className="text-muted-foreground"> at </span>
                      <span className="font-medium">{slot.hour}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {slot.weeksUsed}/{weeksBack} weeks ({slot.consistencyPercent}%)
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quietest Times (Opportunities) */}
        <Card className="glass-card border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-warning" />
              Open Opportunities
            </CardTitle>
            <CardDescription>Weekday slots that are often available</CardDescription>
          </CardHeader>
          <CardContent>
            {quietestSlots.length === 0 ? (
              <p className="text-sm text-muted-foreground">No opportunities identified</p>
            ) : (
              <div className="space-y-2">
                {quietestSlots.map((slot, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                  >
                    <div>
                      <span className="font-medium">{slot.day}</span>
                      <span className="text-muted-foreground"> at </span>
                      <span className="font-medium">{slot.hour}</span>
                    </div>
                    <div className="text-sm">
                      {slot.weeksUsed === 0 ? (
                        <span className="text-warning">Always open</span>
                      ) : (
                        <span className="text-muted-foreground">
                          Open {100 - slot.consistencyPercent}% of weeks
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ScheduleHeatMap;